import { AuditLog } from 'api/Clients/Types/AuditLog'
import { NameDto } from 'api/Common/Types'
import {
  CategoryType,
  ClientDocumentModel,
  ClientDocumentType,
  DocumentCategoryType,
  DocumentStatusId,
  DocumentTypeId,
  MassDocumentCategoryUpdateDto,
  StatusType,
  TagType,
  UpdateClientDocument,
} from 'api/Documents/Types/ClientDocument'
import { ClientDocumentVerificationDto } from 'api/Documents/Types/ClientDocumentVerificationDto'
import { DocumentDto } from 'api/Documents/Types/Document'
import { DocumentStatusHistory } from 'api/Documents/Types/DocumentStatusHistory'
import { DocumentStatusReasonDto } from 'api/Documents/Types/DocumentStatusReasonDto'
import { DocumentUrlNameDto } from 'api/Documents/Types/DocumentUrlNameDto'
import { UpdateDocumentStatusReasonDto } from 'api/Documents/Types/UpdateDocumentStatusReasonDto'
import { UpdateMultipleDocumentStatus } from 'api/Documents/Types/UpdateMultipleDocumentStatus'
import { VerificationDocumentDto } from 'api/Documents/Types/VerificationDocumentDto'
import { PageData } from 'api/Pagination/Types'

import { ApiClient } from './ApiClient'

export class DocumentApiClient {
  constructor(private apiClient: ApiClient) {}

  getDocumentUrl: (id: string) => Promise<DocumentUrlNameDto> = (id) => {
    return this.apiClient.get(`documents/documents/${id}/url`)
  }
  uploadDocumentClient: (data: ClientDocumentModel[]) => Promise<void> = (data) => {
    return this.apiClient.post(`documents/Documents/clients`, { documents: data })
  }
  updateDocumentClient: (data: UpdateClientDocument) => Promise<void> = (data) => {
    return this.apiClient.put(`documents/Documents/${data.id}`, data)
  }

  massUpdateDocuments: (data: MassDocumentCategoryUpdateDto) => Promise<void> = (
    data: MassDocumentCategoryUpdateDto
  ) => {
    return this.apiClient.put('documents/documents/mass-update-category-types', data)
  }
  deleteDocument: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`documents/Documents/${id}`)
  }
  verifyDocument: (id: string) => Promise<void> = (id) => {
    return this.apiClient.put(`documents/documents/${id}/verify`, {})
  }
  verifyDocuments: (documentIds: string[]) => Promise<void> = (documentIds) => {
    return this.apiClient.put(`documents/documents/verify`, { documentIds })
  }
  getDocumentHistory: (id: string) => Promise<DocumentStatusHistory> = (id) => {
    return this.apiClient.get(`documents/documents/${id}/status`)
  }
  getDocumentTypesByCategory: (
    categoryId: string | number
  ) => Promise<{ category: NameDto; types: ClientDocumentType[] }> = (categoryId) => {
    return this.apiClient.get(`documents/Documents/categories/${categoryId}/types`)
  }
  getDocumentCategoriesType: () => Promise<CategoryType[]> = () => {
    return this.apiClient.get(`documents/Documents/categories`)
  }
  getDocumentTagType: () => Promise<TagType[]> = () => {
    return this.apiClient.get(`documents/Documents/tags`)
  }
  getDocumentStatusesType: () => Promise<StatusType[]> = () => {
    return this.apiClient.get(`documents/Documents/statuses`)
  }
  getDocumentStatusReasons: () => Promise<DocumentStatusReasonDto[]> = () => {
    return this.apiClient.get(`documents/Documents/status-reasons`)
  }
  updateDocumentStatusReasons: (
    id: string,
    data: UpdateDocumentStatusReasonDto
  ) => Promise<DocumentStatusReasonDto[]> = (id, data) => {
    return this.apiClient.put(`documents/documents/${id}/status-reasons`, data)
  }
  updateDocumentStatusMassApprove: (
    id: string,
    data: UpdateMultipleDocumentStatus
  ) => Promise<void> = (_id, data) => {
    return this.apiClient.put(`documents/documents/mass-approve`, data)
  }
  updateDocumentStatusMassExpire: (id: string, ids: string[]) => Promise<void> = (_id, ids) => {
    return this.apiClient.put(`documents/documents/mass-expire`, { documentIds: ids })
  }
  updateDocumentStatusMassReject: (
    id: string,
    data: UpdateMultipleDocumentStatus
  ) => Promise<void> = (_id, data) => {
    return this.apiClient.put(`documents/documents/mass-reject`, data)
  }
  getDocumentsByClient: (
    clientId: string,
    pageIndex?: number,
    pageSize?: number,
    category?: number
  ) => Promise<PageData<DocumentDto[]>> = (searchFilter: string, pageIndex = 1, pageSize = 100) => {
    return this.apiClient.get(
      `documents/documents?search=${searchFilter}&PageIndex=${pageIndex}&PageSize=${pageSize}`
    )
  }
  getDocumentsByClientAndTypeId: (
    clientId: string,
    typeId: string,
    pageIndex?: number,
    pageSize?: number
  ) => Promise<PageData<DocumentDto[]>> = (clientId, typeId, pageIndex = 1, pageSize = 100) => {
    return this.apiClient.get(
      `documents/documents?search=(Client == ${clientId}) AND (Type == ${typeId})&PageIndex=${pageIndex}&PageSize=${pageSize}`
    )
  }
  getBankAccountEligibleDocuments: (
    clientId: string,
    pageIndex?: number,
    pageSize?: number
  ) => Promise<PageData<DocumentDto[]>> = (clientId, pageIndex = 1, pageSize = 100) => {
    const categorySearch = `(Category == ${DocumentCategoryType.Payments}) OR (Category == ${DocumentCategoryType.Additional})`
    const typeSearch = `(Type == ${DocumentTypeId.BankStatement}) OR (Type == ${DocumentTypeId.ProofOfDeposit})`
    const search = `(Client == ${clientId}) AND (${categorySearch}) AND (${typeSearch}) AND (Status == ${DocumentStatusId.APPROVED})`
    return this.apiClient.get(
      `documents/documents?search=${search}&PageIndex=${pageIndex}&PageSize=${pageSize}`
    )
  }
  getDocumentReportsByDocumentId: (id: string) => Promise<VerificationDocumentDto[]> = (id) => {
    return this.apiClient.get(`documents/documents/${id}/reports`)
  }
  getClientDocumentVerificationReportById: (id: string) => Promise<ClientDocumentVerificationDto> =
    (id) => {
      return this.apiClient.get(`/documents/documents/report/${id}`)
    }
  getDocumentsAuditLogs: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`documents/documents/${id}/events`)
  }
}
